import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect } from "react";
import { useRetrieveAllTiktokSellerAccounts } from "@/hooks/tiktokSellerAccount.hooks";
import { useTiktokAdvertiserService } from "@/hooks/tiktokAdvertiser.hooks";
import { CheckCircleIcon, InfoIcon, XCircleIcon } from "lucide-react";
import { useToast } from "@/hooks/toast.hooks";
import { TIKTOK_BUSINESS_ACCOUNT_LIST } from "@/hooks/tiktokBusinessAccount.hooks";
import { useQueryClient } from "@tanstack/react-query";
import { CANCELLATION_STATISTICS, FULL_TIKTOK_ORDER_BY_ID, LITE_CANCELLED_STATISTICS, LITE_PRODUCT_SKU_STATISTICS, LITE_PRODUCT_STATISTICS, LITE_SAMPLE_STATISTICS, ORDERS_WITH_ESTIMATES, LITE_DASHBOARD_STATISTICS } from "@/hooks/tiktokOrder.hooks";
import { LITE_SETTLED_DASHBOARD_STATISTICS } from "@/hooks/tiktokSkuSettledPeriod.hooks";

interface ManageAdvertiserToSellerAccountModalProps {
  trigger: React.ReactNode;
  advertiser: {
    id: number;
    name: string | null;
    advertiserAccountType: "AUCTION" | "RESERVATION";
    currency: string;
    role:
      | "ROLE_ADVERTISER"
      | "ROLE_CHILD_ADVERTISER"
      | "ROLE_CHILD_AGENT"
      | "ROLE_AGENT";
    description: string | null;
    country: string;
    company: string | null;
    advertiserId: string;
    tiktokAdvertiserToSellerAccounts: {
      tiktokSellerAccount: {
        id: number;
        openId: string;
        sellerName: string;
        sellerBaseRegion: string;
      };
    }[];
  };
}

export const ManageAdvertiserToSellerAccountModal = ({
  advertiser,
  trigger,
}: ManageAdvertiserToSellerAccountModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSellerAccounts, setSelectedSellerAccounts] = useState<
    number[]
  >([]);
  const { data: sellerAccounts, isLoading } =
    useRetrieveAllTiktokSellerAccounts();
  const { upsertAdvertiserToSellerAccounts } = useTiktokAdvertiserService();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (sellerAccounts) {
      // If no seller accounts are linked, select all by default
      if (!advertiser.tiktokAdvertiserToSellerAccounts.length) {
        setSelectedSellerAccounts([]);
      } else {
        // Otherwise set the currently linked accounts
        setSelectedSellerAccounts(
          advertiser.tiktokAdvertiserToSellerAccounts.map(
            (link) => link.tiktokSellerAccount.id
          )
        );
      }
    }
  }, [sellerAccounts, advertiser]);

  const handleSubmit = async () => {
    setSaving(true);
    const response = await upsertAdvertiserToSellerAccounts({
      advertiserId: advertiser.id,
      tiktokSellerAccountIds: selectedSellerAccounts,
    });

    setSaving(false);

    if (!response.success) {
      toast({
        icon: <XCircleIcon size={12} className={"text-red-500"} />,
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description:
          response.errorMessage || "Failed to update seller account links",
      });
      return;
    }

    setIsOpen(false);
    toast({
      icon: <CheckCircleIcon size={12} className={"text-green-500"} />,
      variant: "default",
      title: "Success",
      description: "Seller account links have been updated successfully.",
      duration: 2000,
    });

    queryClient.invalidateQueries({ queryKey: [TIKTOK_BUSINESS_ACCOUNT_LIST] });
    queryClient.invalidateQueries({ queryKey: [LITE_DASHBOARD_STATISTICS] });
    queryClient.invalidateQueries({ queryKey: [ORDERS_WITH_ESTIMATES] });
    queryClient.invalidateQueries({ queryKey: [FULL_TIKTOK_ORDER_BY_ID] });
    queryClient.invalidateQueries({ queryKey: [LITE_SETTLED_DASHBOARD_STATISTICS] });
  };

  const toggleSellerAccount = (sellerId: number) => {
    setSelectedSellerAccounts((current) =>
      current.includes(sellerId)
        ? current.filter((id) => id !== sellerId)
        : [...current, sellerId]
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[600px] p-0">
        <div>
          <div className="border-b flex gap-10 items-center justify-between px-6 py-4">
            <div className="flex flex-col">
              <h3 className="font-semibold text-lg">Configure Seller Connections</h3>
              <div className="flex items-center gap-4 text-blue-500">
                <InfoIcon className="h-4 w-4" />
                <p className="text-sm font-normal text-blue-500">
                  Select the seller accounts that we should send spend data to.
                </p>
              </div>
            </div>
            <Button
              onClick={handleSubmit}
              className="w-auto bg-blue-500 text-white hover:bg-blue-600 mr-4"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>

          <div className="p-6">
            <div className="flex flex-col gap-4">
              {isLoading ? (
                <div>Loading seller accounts...</div>
              ) : (
                sellerAccounts?.map((seller) => (
                  <div
                    key={seller.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{seller.sellerName}</span>
                      <span className="text-sm text-muted-foreground">
                        Region: {seller.sellerBaseRegion}
                      </span>
                    </div>
                    <Switch
                      checked={selectedSellerAccounts.includes(seller.id)}
                      onCheckedChange={() => toggleSellerAccount(seller.id)}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
